import type { LandingAgentEvent, LandingAgentTurn } from './landingAgentSlice';

/**
 * C8 (#1672): the answer's words as they are written. An `answer_delta` frame is live only — never traced, stored or
 * replayed — and carries one displayed field: the synthesis headline, or one formatter highlight (by its index). The
 * turn holds what has streamed in `streaming`, outside `events`; a part's final frame replaces it, and a terminal
 * frame or the ladder clears what no final frame closed, so a finished turn is exactly what today's frames build.
 */
export type AnswerDeltaPart =
  | { kind: 'synthesis'; field: 'headline' }
  | { kind: 'formatter'; sub_task_id: string; field: 'highlights'; index: number };

/** One streamed part: its intact text, the `seq` it expects next, and whether a gap froze it. */
export interface StreamingPart { part: AnswerDeltaPart; text: string; next: number; frozen: boolean }

/** A turn's streamed words (transient: never on the thread snapshot). `closed` names the parts a final frame closed —
 *  `synthesis` or `formatter:<sub_task_id>` — whose later deltas are dropped. */
export interface AnswerStreaming { parts: Record<string, StreamingPart>; closed: string[] }

/** The words a stopped answer had shown (each part's intact prefix), kept in the asker's own snapshot. */
export interface StoppedWords { headline: string | null; highlights: string[] }

const isSeq = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

/** The part a delta names, of its declared shape only; null for anything else. */
function partOf(raw: unknown): AnswerDeltaPart | null {
  const p = raw as Record<string, unknown> | null;
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  if (p.kind === 'synthesis' && p.field === 'headline') return { kind: 'synthesis', field: 'headline' };
  if (p.kind === 'formatter' && p.field === 'highlights' && typeof p.sub_task_id === 'string' && p.sub_task_id
    && isSeq(p.index)) {
    return { kind: 'formatter', sub_task_id: p.sub_task_id, field: 'highlights', index: p.index };
  }
  return null;
}

/** What a part's final frame closes: the headline, or every highlight of one sub-task. */
function ownerOf(part: AnswerDeltaPart): string {
  return part.kind === 'synthesis' ? 'synthesis' : `formatter:${part.sub_task_id}`;
}

function keyOf(part: AnswerDeltaPart): string {
  return part.kind === 'synthesis' ? 'synthesis' : `formatter:${part.sub_task_id}:${part.index}`;
}

/**
 * One `answer_delta` applied to its turn: the turn's new `streaming`, or null when the delta changes nothing. Its text
 * is appended only when its `seq` is exactly the next its part expects (0 for a part this socket has not seen); any
 * other `seq` freezes the part at its intact prefix, so what is shown is always a prefix of the final field. A delta
 * of any other shape, for another session, for a closed part, or once the turn stopped running is dropped.
 */
export function reduceAnswerDelta(turn: LandingAgentTurn, evt: LandingAgentEvent): AnswerStreaming | null {
  if (evt.type !== 'answer_delta' || turn.status !== 'running' || turn.stopped || turn.result?.ladder) return null;
  const part = partOf(evt.part);
  if (!part || !isSeq(evt.seq) || typeof evt.text !== 'string') return null;
  if (turn.sessionId && evt.session_id !== turn.sessionId) return null;
  const streaming: AnswerStreaming = turn.streaming || { parts: {}, closed: [] };
  if (streaming.closed.includes(ownerOf(part))) return null;
  const key = keyOf(part);
  const held = streaming.parts[key];
  if (held?.frozen) return null;
  const expected = held ? held.next : 0;
  const next: StreamingPart = evt.seq === expected
    ? { part, text: (held?.text || '') + evt.text, next: expected + 1, frozen: false }
    : { part, text: held?.text || '', next: expected, frozen: true };
  return { ...streaming, parts: { ...streaming.parts, [key]: next } };
}

/** The turn's `streaming` once a frame lands: a part's final frame closes it (its text replaced by the frame's), even
 *  when none of it has streamed yet, so a delta the relay delivers after it is dropped (#1760); a terminal frame or the
 *  ladder clears every part. Undefined when the frame closes nothing. */
export function streamingAfterFrame(streaming: AnswerStreaming | null | undefined,
  evt: LandingAgentEvent): AnswerStreaming | null | undefined {
  if (['mission_complete', 'mission_error', 'mission_cancelled', 'ladder'].includes(evt.type)) {
    return streaming ? null : undefined;
  }
  const owner = evt.type === 'synthesis_result' || evt.type === 'synthesis_error' ? 'synthesis'
    : evt.type === 'formatter_result' && typeof evt.sub_task_id === 'string' ? `formatter:${evt.sub_task_id}` : null;
  if (!owner) return undefined;
  if (!streaming) return { parts: {}, closed: [owner] };
  const parts = Object.fromEntries(Object.entries(streaming.parts).filter(([, p]) => ownerOf(p.part) !== owner));
  return { parts, closed: streaming.closed.includes(owner) ? streaming.closed : [...streaming.closed, owner] };
}

/** The words a turn's open parts show: the headline, and the highlights grouped by sub-task (in the order each was
 *  first seen), each sub-task's by index. Empty parts are left out. */
export function streamedWords(streaming: AnswerStreaming | null | undefined): StoppedWords {
  const parts = Object.values(streaming?.parts || {}).filter((p) => p.text);
  const headline = parts.find((p) => p.part.kind === 'synthesis')?.text || null;
  const order: string[] = [];
  for (const p of parts) if (p.part.kind === 'formatter' && !order.includes(p.part.sub_task_id)) order.push(p.part.sub_task_id);
  const highlights = order.flatMap((id) => parts
    .filter((p) => p.part.kind === 'formatter' && p.part.sub_task_id === id)
    .sort((a, b) => (a.part as { index: number }).index - (b.part as { index: number }).index)
    .map((p) => p.text));
  return { headline, highlights };
}

/** The words a Stop keeps (null when nothing had streamed): what the card showed of each part, as it showed it. */
export function stoppedWordsOf(streaming: AnswerStreaming | null | undefined): StoppedWords | null {
  const words = streamedWords(streaming);
  return words.headline || words.highlights.length ? words : null;
}

/** A turn as a thread snapshot keeps it: without its streamed parts, which are live only. */
export function withoutStreaming<T extends { streaming?: AnswerStreaming | null }>(turn: T): T {
  if (!turn || !('streaming' in turn)) return turn;
  const { streaming, ...rest } = turn;
  return rest as T;
}
